import axios from 'axios';

// API Basis-URL ermitteln
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8000/api/v1'
  : (process.env.REACT_APP_API_URL as string | undefined);

// Axios Instanz erstellen
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor für Azure AD Authentication mit OIDC
api.interceptors.request.use(
  async (config) => {
    try {
      const { userManager } = await import('../context/AuthContextOIDC');

      if (!userManager) {
        return config;
      }

      const user = await userManager.getUser();

      if (user && user.access_token) {
        // Prüfe Token-Gültigkeit
        const payload = JSON.parse(atob(user.access_token.split('.')[1]));
        const exp = payload.exp;

        if (exp && exp * 1000 > Date.now()) {
          config.headers['Authorization'] = `Bearer ${user.access_token}`;
        } else {
          try {
            const renewedUser = await userManager.signinSilent();
            if (renewedUser && renewedUser.access_token) {
              config.headers['Authorization'] = `Bearer ${renewedUser.access_token}`;
            }
          } catch (renewError) {
            console.warn('⚠️ API: Token abgelaufen und Silent Renewal fehlgeschlagen');
          }
        }
      }
    } catch (importError) {
      console.warn('⚠️ API: OIDC UserManager nicht verfügbar');
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor für Authentication Errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Network Errors (Backend offline/crashed)
    if (!error.response && (error.code === 'ERR_NETWORK' || error.code === 'ERR_CONNECTION_REFUSED' || error.code === 'ERR_EMPTY_RESPONSE')) {
      console.warn('🔌 API: Network Error erkannt - Backend wahrscheinlich offline');

      const errorCount = parseInt(sessionStorage.getItem('network_error_count') || '0') + 1;
      sessionStorage.setItem('network_error_count', errorCount.toString());

      if (errorCount >= 3) {
        console.log('🚪 API: Automatisches Logout nach wiederholten Network Errors');

        try {
          const { userManager } = await import('../context/AuthContextOIDC');
          if (userManager) {
            await userManager.removeUser();
            console.log('✅ API: Token gelöscht nach Network Errors');
          }

          localStorage.removeItem('access_token');
          sessionStorage.removeItem('access_token');
          sessionStorage.removeItem('network_error_count');
        } catch (cleanupError) {
          console.warn('⚠️ API: Token-Cleanup fehlgeschlagen:', cleanupError);
        }

        window.location.href = '/login';
        return Promise.reject(error);
      }

      return Promise.reject(error);
    }

    if (error.response) {
      sessionStorage.removeItem('network_error_count');
    }

    if (error.response?.status === 401) {
      console.warn('🔒 API: 401 Unauthorized - lösche Token und leite zu Login um');

      try {
        const { userManager } = await import('../context/AuthContextOIDC');
        if (userManager) {
          await userManager.removeUser();
          console.log('✅ API: Token gelöscht nach 401');
        }
      } catch (cleanupError) {
        console.warn('⚠️ API: Token-Cleanup fehlgeschlagen:', cleanupError);
      }

      window.location.href = '/login';
    }

    if (error.response?.status === 403) {
      const errorMessage = error.response?.data?.detail || 'Zugriff verweigert';

      if (errorMessage.includes('App-Zugriff wurde entfernt') || 
          errorMessage.includes('Admin-App-Zugriff wurde entfernt') ||
          errorMessage.includes('nicht aktiv')) {

        console.warn('🚫 API: 403 App-Zugriff entfernt - lösche Token und leite weiter');

        try {
          const { userManager } = await import('../context/AuthContextOIDC');
          if (userManager) {
            await userManager.removeUser();
            console.log('✅ API: Token gelöscht nach App-Zugriff entfernt');
          }
        } catch (cleanupError) {
          console.warn('⚠️ API: Token-Cleanup fehlgeschlagen:', cleanupError);
        }

        window.location.href = '/login';
      } else {
        console.warn('🔒 API: 403 Forbidden (andere Ursache):', errorMessage);

        const logoutMessages = [
          'Sie müssen von einem Administrator zu einer Organisation eingeladen werden',
          'Ihr Zugriff wurde automatisch entfernt',
          'Sie müssen von einem Administrator eingeladen werden'
        ];

        const shouldLogout = logoutMessages.some(msg => 
          errorMessage.toLowerCase().includes(msg.toLowerCase())
        );

        if (shouldLogout) {
          console.log('🚪 API: Automatisches Logout - Benutzer nicht autorisiert');

          try {
            localStorage.removeItem('access_token');
            sessionStorage.removeItem('access_token');
            console.log('✅ API: Token gelöscht nach Autorisierungsfehler');
          } catch (cleanupError) {
            console.warn('⚠️ API: Token-Cleanup fehlgeschlagen:', cleanupError);
          }

          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);


